import PropTypes from 'prop-types';
import { forwardRef, useEffect } from 'react';
import { useLocation, Link, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Avatar, Chip, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material';

// project import
import { activeItem } from 'store/reducers/menu';

// ==============================|| NAVIGATION - LIST ITEM ||============================== //

const NavItem = ({ item, level }) => {
  const theme = useTheme();
  const params = useParams();
  const dispatch = useDispatch();
  const menu = useSelector((state) => state.menu);
  const { drawerOpen, openItem } = menu;

  let itemTarget = '_self';
  if (item.target) {
    itemTarget = '_blank';
  }

  let listItemProps = { component: forwardRef((props, ref) => <Link {...props} to={item.url} target={itemTarget} ref={ref} />) };
  if (item?.external) {
    listItemProps = { component: 'a', href: item.url, target: itemTarget };
  }

  const Icon = item.icon;
  const itemIcon = item.icon ? <Icon color="color" style={{ fontSize: drawerOpen ? '1rem' : '1.25rem' }} /> : false;

  const isSelected = openItem.findIndex((id) => id === item.id) > -1;
  const { pathname } = useLocation();

  // active menu item on page load
  useEffect(() => {
    let formatPathname = pathname;
    if (params.id) {
      formatPathname = formatPathname.substring(0, formatPathname.indexOf(`/${params.id}`));
    }

    if (formatPathname === item.url) {
      dispatch(activeItem({ openItem: [item.id] }));
    }
    // eslint-disable-next-line
  }, [pathname]);

  const textColor = 'white';

  return (
    <ListItemButton
      {...listItemProps}
      disabled={item.disabled}
      selected={isSelected}
      sx={{
        zIndex: 1201,
        margin: 2,
        padding: 2,
        borderRadius: '35px',
        pl: drawerOpen ? `${level * 28}px` : 1.5,
        ...(drawerOpen && {
          '&:hover': {
            background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.primary.darker})`
          },
          '&.Mui-selected': {
            color: 'white',
            background: `linear-gradient(to right, ${theme.palette.primary.darker}, ${theme.palette.primary.main})`,
            '&:hover': {
              background: `linear-gradient(to right, ${theme.palette.primary.darker}, ${theme.palette.primary.main})`
            }
          }
        }),
        ...(!drawerOpen && {
          '&:hover': {
            bgcolor: 'transparent'
          },
          '&.Mui-selected': {
            '&:hover': {
              bgcolor: 'transparent'
            },
            bgcolor: 'transparent'
          }
        })
      }}
    >
      {itemIcon && (
        <ListItemIcon
          sx={{
            fontWeight: 'bold',
            color: textColor,
            textColor: textColor
          }}
        >
          {itemIcon}
        </ListItemIcon>
      )}
      {(drawerOpen || (!drawerOpen && level !== 1)) && (
        <ListItemText
          sx={{ ml: 1 }}
          primary={
            <Typography variant="h6" sx={{ color: textColor, fontWeight: 'bold', lineHeight: 'normal' }}>
              {item.title}
            </Typography>
          }
        />
      )}
      {(drawerOpen || (!drawerOpen && level !== 1)) && item.chip && (
        <Chip
          color={item.chip.color}
          variant={item.chip.variant}
          size={item.chip.size}
          label={item.chip.label}
          avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
        />
      )}
    </ListItemButton>
  );
};

NavItem.propTypes = {
  item: PropTypes.object,
  level: PropTypes.number
};

export default NavItem;
